import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { APPOINTMENT_FILES } from "../../../enums/QueryKeys";
import { SnackbarContext } from "../../../contexts/Snackbar";
import { snackbarSeverity } from "../../../hooks/useSnackbar";
import useUser from "../../../hooks/useUser";
import useConfirm from "../../../hooks/useConfirm";
import { GlobalDataContext } from "../../../contexts/GlobalData";
import { DELETE_DOCUMENTS } from "../../../enums/PermissionTitles";
import { deleteFile } from "../../../enums/queryFunction/Delete";

// Shared by the grid and table views of an appointment's files
export default function useDeleteAppointmentFile(appointmentId) {
  const { roles } = useUser();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const [deletingFileId, setDeletingFileId] = React.useState(null);
  const { ConfirmationDialog, confirm, getMessage } = useConfirm(
    t("AreYouSure"),
    t("Accept"),
    t("Cancel"),
    "warning"
  );

  const canDelete = !roles.user && isPermissionAllowed(DELETE_DOCUMENTS);

  const { mutate } = useMutation({
    mutationFn: deleteFile,
    onSuccess: (_, fileId) => {
      queryClient.setQueryData(
        [APPOINTMENT_FILES + appointmentId],
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.filter((item) => item.fileId !== fileId);
        }
      );
      handleSnackbar("successMessage", snackbarSeverity.SUCCESS);
    },
    onError: (e) => {
      handleSnackbar(e?.response?.data, snackbarSeverity.ERROR);
    },
    onSettled: () => setDeletingFileId(null),
  });

  async function handleDeleteFile(fileId, name) {
    if (!canDelete) return;
    getMessage(
      `${t("DeleteFileConfirmation")} "${name}"? ${t("ThisActionIsPermanent")}`
    );
    const ans = await confirm();
    if (ans) {
      setDeletingFileId(fileId);
      mutate(fileId);
    }
  }

  return { handleDeleteFile, deletingFileId, canDelete, ConfirmationDialog };
}
